import MultiMessageError from './MultiMessageError.js'

/**
 * Method for interacting with remote api endpoint. Response data must be json
 * formatted. 
 *
 * Execute ajax request with <method> to <endpoint>, with provided <data> as
 * payload.  If <method> other than GET Content-Type:application/json is
 * specified for request.
 *
 * Response is parsed as json and the result is returned from the promise
 * resolver.
 * 
 * @async
 * @param {string} method
 * @param {string} endpoint
 * @param {object=} data
 * @return {Promise}
 * @throws {Error} If response is not json string.
 * @throws {Error} If no response from server in 20 sec.
 * @throws {Error} If empty response from server.
 * @throws {Error} If server responded with a non-`200` status code. 
 * @throws {MultiMessageError} If error received as property on server
 * response.
 */
export default function apiFetch (method, endpoint, data) {
  const enc = encodeURIComponent

  const queryString = (data && method !== 'GET') ? '' : Object.keys(data)
    .map(key => `${enc(key)}=${enc(data[key])}`)
    .join(';')
    .replace(/^/, '?')

  const headers =
    method !== 'GET' ? {'Content-Type': 'application/json'} : {}

  const body = method !== 'GET' ? JSON.stringify(data) : undefined

  return Promise.race([
    fetch(`/gyrobase/API/${endpoint}${queryString}`, {
      method, headers, body, credentials: 'same-origin',
    }),
    new Promise((resolve, reject) => {
      return setTimeout(() => reject(new Error(`Request timed out.`)), 20000)
    })
  ]).then(response => {
    return response.text()
      .then(text => {
        let data, parseError
        try { data = JSON.parse(text) }
        catch (err) { parseError = true }

        if (!parseError && response.ok) return data

        if (response.ok && (parseError || text.match(/^\s*$/))) {
          // console.error(`Unexpected response: '${text}'`)
          throw new Error(`Server returned unexpected response.`)
        }

        if (data && data.errors) {
          throw new MultiMessageError(data.errors)
        }

        throw new Error(`Problem communicating with server: ${response.status}, ${response.statusText}`)
      })
  })
}
